import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FileSignature, Bell, ImageIcon } from "lucide-react";
import { getMyPrefs, putMyPrefs, putTrainerStatus, errMsg, fmtSaleDate, fetchDraftMediaBlob } from "@/lib/applyApi";
import { BlobImg } from "@/components/apply/MediaStep";
import { ContactPreference, YesNo } from "@/components/apply/Steps";
import { Button } from "@/components/ui/button";
import { AccountEmail } from "@/components/AccountEmail";
import { AuctionClose } from "@/lib/brand";

const NEXT = {
  submitted: "We're reviewing your horse — no action needed",
  under_review: "We're reviewing your horse — no action needed",
  more_info_requested: "More Information Required — Read message & reply",
  approved: "Application Approved",
  declined: "Application declined — see messages",
};

export function SaleDateLine({ s, testId }) {
  return s?.close_date ? (
    <div data-testid={testId} className="text-sm text-stone-900">
      {s.start_date && <div className="text-xs text-stone-600">Bidding starts {fmtSaleDate(s.start_date)}{s.start_time_label ? ` · ${s.start_time_label}` : ""}</div>}
      <strong>{fmtSaleDate(s.close_date)}</strong>
      <div className="text-xs text-stone-600">Closes at {s.close_time_label || <AuctionClose />}{s.set_by === "seller" ? " · seller-selected" : ""}</div>
    </div>
  ) : <div data-testid={testId} className="text-sm font-semibold text-stone-500">Not Scheduled Yet</div>;
}

export function nextAction(a) {
  if (a.status === "draft") return "Next: finish and submit your application";
  if (a.status !== "approved") return NEXT[a.status];
  if (a.contract_status !== "signed") return a.my_role === "trainer" ? ({ awaiting_seller: "Waiting for the owner to sign the agreement", amendment_awaiting_seller: "Waiting for the owner to sign the amendment" }[a.contract_status] || "Advertising Agreement being prepared")
    : { awaiting_seller: "Next: review & sign your Advertising Agreement", amendment_awaiting_seller: "Next: review & sign the amended agreement" }[a.contract_status] || "Advertising Agreement being prepared";
  return { waiting_on_seller: "Next: complete your Horse Ad Template", draft: "Next: finish your Horse Ad Template", changes_requested: "Next: update your Horse Ad Template", submitted: "Ad submitted — awaiting review", approved: "Ad approved" }[a.ad_template_status] || "Your ad is being prepared";
}

export function Thumb({ a }) {
  const photos = (a.media || []).filter((m) => m.kind === "photo");
  const m = photos.find((p) => p.is_main) || photos[0];
  const load = useCallback(() => fetchDraftMediaBlob(a.id, null, m?.id, m?.rotation), [a.id, m?.id, m?.rotation]);
  if (!m || a.status === "draft") return <div className="h-16 w-16 shrink-0 rounded-xl bg-stone-100 flex items-center justify-center"><ImageIcon className="h-5 w-5 text-stone-400" /></div>;
  return <div className="h-16 w-16 shrink-0 rounded-xl overflow-hidden bg-stone-100"><BlobImg load={load} alt={a.horse.horse_name || "Horse"} className="h-full w-full object-cover" /></div>;
}

export function ContactPrefsCard() {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { getMyPrefs().then((d) => setP(d || {})).catch(() => setP({})); }, []);
  if (!p) return null;
  const save = async () => {
    setBusy(true);
    try {
      setP(await putMyPrefs({ contact_preference: p.contact_preference, sms_consent: !!p.sms_consent }));
      toast.success("Contact preferences saved");
    } catch (e) {
      toast.error(errMsg(e, "Could not save"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section id="contact-prefs-card" data-testid="contact-prefs-card" className="bg-white border border-stone-200 rounded-xl p-4 mb-4 scroll-mt-20">
      <h2 className="font-semibold text-stone-900 flex items-center gap-2 mb-3"><Bell className="h-4 w-4 text-amber-800" /> Account &amp; Contact Preferences</h2>
      <AccountEmail />
      <ContactPreference value={p.contact_preference} consent={p.sms_consent} onChange={(v) => setP((s) => ({ ...s, ...v }))} />
      {p.sms_opted_out_at && !p.sms_consent && <p className="mt-2 text-xs text-stone-500">You opted out of text messages on {new Date(p.sms_opted_out_at).toLocaleDateString()}.</p>}
      <p className="mt-2 text-xs text-stone-500">To stop texts, choose Email and save.</p>
      <Button data-testid="save-contact-prefs" onClick={save} disabled={busy || !p.contact_preference} className="mt-3 h-11 rounded-full bg-amber-800 hover:bg-amber-900 text-white">Save preferences</Button>
      <div id="account-trainer-status" data-testid="account-trainer-status" className="mt-4 pt-4 border-t border-stone-200 scroll-mt-20">
        <YesNo id="account-is-trainer" label="Are you a horse trainer?" value={p.is_trainer}
          onChange={(v) => putTrainerStatus(v).then((r) => { setP((s) => ({ ...s, ...r })); toast.success("Trainer status saved"); }).catch((e) => toast.error(errMsg(e, "Could not save")))} />
      </div>
    </section>
  );
}

export function ContractsPlaceholder() {
  return (
    <section data-testid="contracts-placeholder" className="bg-stone-50 border border-stone-200 rounded-xl p-4 mb-8">
      <h2 className="font-semibold text-stone-900 flex items-center gap-2"><FileSignature className="h-4 w-4 text-amber-800" /> Contracts</h2>
      <p className="text-sm text-stone-500 mt-1">Each horse's Horse Sale Advertising Agreement is on its own card: open the horse from My Horses on your dashboard to review, sign or download signed copies.</p>
    </section>
  );
}
